'use client';
import {
  privateQueryContext,
  privateQueryOptions,
} from '@eliadmin/core/src/utils/private-query-context';

import { useQuery } from '@tanstack/react-query';

import { fetchOrderInfo, type OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCustomerOrders } from '@eliadmin/core/src/api/customers/customers-api';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { FetchError } from '@eliadmin/core/src/api/fetcher';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

/** Orders per request; the loading follows `meta.last_page` whatever the API pages by. */
const PAGE_SIZE = 50;

function timestamp(value: string) {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * All orders of the signed-in customer, newest first. The API pages them, but the filter counts
 * and the search need the whole list, so every page is loaded (DLZ `orders-data.ts`).
 */
export function useCustomerOrders() {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken ?? '');
  const customerId = useCustomerStore(s => s.me?.customer.id);

  return useQuery({
    queryKey: [
      CacheKey.ORDERS,
      ...privateQueryContext(locale, locale, customerId, authToken, 'all'),
    ],
    ...privateQueryOptions,
    enabled: !!authToken,
    queryFn: async () => {
      const page = (number: number) =>
        fetchCustomerOrders({ locale, authToken, query: { page: number, results: PAGE_SIZE } });
      const first = await page(1);
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, first.meta.last_page - 1) }, (_, index) =>
          page(index + 2),
        ),
      );
      return [first, ...rest]
        .flatMap(response => response.data)
        .sort((a, b) => timestamp(b.created_at) - timestamp(a.created_at));
    },
  });
}

/**
 * `/orders/info` of one order (addresses, delivery, payment, prices), `null` when unknown. The
 * endpoint looks the order up by number and e-mail – the account's e-mail. A refused lookup (4xx)
 * is not retried. `enabled: false` keeps a list card from asking for it.
 */
export function useOrderInfo(identifier: string, enabled = true) {
  const locale = useLocaleStore(s => s.currentLocale);
  const email = useCustomerStore(s => s.me?.customer.email ?? '');
  const authToken = useCustomerStore(s => s.me?.authToken);

  return useQuery({
    queryKey: [
      CacheKey.ORDER_INFO,
      ...privateQueryContext(locale, locale, identifier, email, authToken),
    ],
    ...privateQueryOptions,
    enabled: enabled && !!email && !!identifier,
    queryFn: () => fetchOrderInfo({ locale, identifier, email }).then(response => response.data),
    retry: (failures, error) =>
      !(error instanceof FetchError && error.status < 500) && failures < 3,
  });
}

export function usePrice() {
  const { currency, currency_settings: currencySettings } = useGlobalStore(s => s.generalLayout);
  return (value: number) => formatPrice(value, currency, currencySettings);
}

/** The number the customer knows the order by – also the key of the detail URL and `/orders/info`. */
export function orderNumber(order: Pick<CustomerOrder, 'id' | 'identifier' | 'order_identifier'>) {
  return order.identifier || order.order_identifier || String(order.id);
}

export function orderTotal(order: Pick<CustomerOrder, 'price' | 'price_with_vat'>) {
  return order.price_with_vat ?? order.price;
}

/** Lines and pieces of an order („3 produkty, 4 ks“). */
export function orderCounts(items: { quantity: number }[]) {
  return {
    products: items.length,
    pieces: items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
  };
}

function validDate(value: string | null | undefined) {
  const date = new Date(value ?? '');
  return Number.isNaN(date.getTime()) ? null : date;
}

/** „21. 10. 2026“ of an API timestamp (the viewer's time zone), empty when it cannot be read. */
export function orderDate(value: string | null | undefined) {
  return (
    validDate(value)?.toLocaleDateString('sk', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    }) ?? ''
  );
}

/** „11:24“ of an API timestamp, empty when it cannot be read. */
export function orderTime(value: string | null | undefined) {
  return validDate(value)?.toLocaleTimeString('sk', { hour: 'numeric', minute: '2-digit' }) ?? '';
}

/** Whether the timestamp falls on the viewer's today. */
export function isToday(value: string | null | undefined) {
  const date = validDate(value);
  return !!date && date.toDateString() === new Date().toDateString();
}

/** Lower case without diacritics – the search matches „cierny“ as well as „čierny“. */
export function searchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/*
 * Fields some backends send on `/orders/info` that the core type does not declare (the harness
 * mock sends them; the live shape is not captured yet). Read defensively, rendered only when set.
 */
type OrderInfoExtras = {
  phone?: unknown;
  name2?: unknown;
  surname2?: unknown;
  company2?: unknown;
  ico?: unknown;
  dic?: unknown;
  ic_dph?: unknown;
  company?: unknown;
};

function extra(info: OrderInfo, key: keyof OrderInfoExtras) {
  const value = (info as OrderInfo & OrderInfoExtras)[key];
  return typeof value === 'string' ? value.trim() : '';
}

export function orderExtras(info: OrderInfo) {
  return {
    phone: extra(info, 'phone'),
    recipient: [extra(info, 'name2'), extra(info, 'surname2')].filter(Boolean).join(' '),
    recipientCompany: extra(info, 'company2'),
    company: extra(info, 'company'),
    ico: extra(info, 'ico'),
    dic: extra(info, 'dic'),
    icDph: extra(info, 'ic_dph'),
  };
}

/** The carrier's tracking link of the shipment, when the API sends one (`shipment.tracking_url`). */
export function trackingUrl(info: OrderInfo | null | undefined) {
  const url = (info?.shipment as { tracking_url?: unknown } | null | undefined)?.tracking_url;
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim()) ? url.trim() : '';
}
